import React, { FC } from 'react';
import cx from 'classnames';

import {
  TagContainer,
  TagContent,
  TagEmoji,
  TagImage,
  TagList,
  TagV2Size,
  TagV2Variant,
} from './TagV2.styles';

export type TagV2Props = {
  /** Content of the tag, typically just text */
  children: React.ReactNode;

  /** Optional class(es) to add to the tag */
  className?: string | { [className: string]: boolean };

  /** Optional variant: default White */
  variant?: TagV2Variant;

  /** Optional size: default: standards (20px tall tag) */
  size?: TagV2Size;

  /** Optional emjoi to include before the tag text, for example, a star (⭐️) or  calendar (📅) */
  emoji?: {
    symbol: string;
    alt?: string;
  };

  /**
   * PREFER EMOJI: optional image  to display before the text / children.  However, the Emoji
   * is preferred (as its a font) and it fits better than an image does in the tag.
   * */
  image?: {
    src: string;
    alt: string;
  };
};

/**
 * Tags are used to indicate a status, attribute, or a state.
 *
 * Tags not an interactive elements; they are not clickable and do not have a
 * hover state (tool tip being the exception).
 *
 * Keep the copy as short as possible, under 20 characters if possible. Tag copy should
 * be functional, objective, and to-the-point and not contain overly flowery language.
 *
 * TODO:
 *
 * - Tool tip
 * - Aria
 */
export const TagV2: FC<TagV2Props> = ({
  children,
  image,
  className,
  variant = TagV2Variant.WHITE,
  size = TagV2Size.REGULAR,
  emoji,
}) => {
  return (
    <TagContainer variant={variant} size={size} emoji={emoji} className={cx(className)}>
      <TagContent>
        {image && <TagImage src={image.src} alt={image.alt} height={14} width={14} />}
        {emoji && (
          <TagEmoji size={size} role="img" aria-label={emoji.alt}>
            {emoji.symbol}
          </TagEmoji>
        )}
        {children}
      </TagContent>
    </TagContainer>
  );
};

export default TagV2;

export type TagV2ListProps = {
  /** Tags to display */
  children: React.ReactNode;

  /** Optional class(es) to add to the tag list */
  className?: string | { [className: string]: boolean };
};

/** A wrapper when you want to display multiple tags */
export const TagV2List: FC<TagV2ListProps> = ({ children, className }) => {
  return <TagList className={cx(className)}>{children}</TagList>;
};

export { TagV2Size, TagV2Variant } from './TagV2.styles';
